import { Box, 
         IconButton, 
         Typography, 
         useTheme,
         Snackbar,
         Alert,
         Menu,
         MenuItem,
         ListItemIcon,
         ListItemText,
         FormControl,
         InputLabel,
         Select
       }                                    from "@mui/material"
import { BglAlertDialog }                   from "components/dialog/BglAlertDialog"
import React, 
       { useMemo, 
         useState, 
         useCallback
       }                                    from "react"
import { useSelector, 
         useDispatch 
       }                                    from "react-redux"
import { APIData }                          from "types/cms/frontendTypes"
import { BookOpen, 
         Eye, 
         ListTree,
         Pencil,
         Trash2
       }                                    from "lucide-react"
import { selectPublishedQuestions }         from "store/dataReducers/surveyQBSlice"
import { BglCloseOutlinedSmallButton, 
         BglPrimaryOutlinedSmallButton, 
         BglSecondaryOutlinedSmallButton 
       }                                    from "components/button/BglButtons"
import { updateTemplatesAction,
         selectPublishedTemplates,
         selectUnsavedTemplates,
         setUnsavedTemplateAction,
         discardUnsavedTemplateAction,
         selectReviewWorkflow,
         publishTemplatesAction,
         unpublishTemplatesAction,
         discardAllDraftTemplatesAction
       }                                    from "store/dataReducers/surveyTemplateSlice"
import { SurveyTemplateAPI }                from "apis/surveyTemplateAPI"
import { useGlobalConfig }                  from "context/GlobalContext"
import { selectCMSSelectedPilotDocId }      from "store/dataReducers/cmsPilotSlice"
import { SurveyTemplateUtils }              from "../utils/SurveyTemplateUtils"
import { SurveyTemplateQuestionItem, 
         QuestionTreeNode 
       }                                    from "./SurveyTemplateQuestionItem"
import { ImportQuestionBankDrawer }         from "./ImportQuestionBankDrawer"
import { SurveyTemplateEditNameDialog }     from "./SurveyTemplateEditNameDialog"
import { SurveyTemplateEditQuestionTitleDialog } from "./SurveyTemplateEditQuestionTitleDialog"
import MoreVertIcon                         from '@mui/icons-material/MoreVert'
import SaveIcon                             from '@mui/icons-material/Save';
import HistoryIcon                          from '@mui/icons-material/History';
import { VerificationWorkflowUtils } from "framework/utility/VerificationWorkflowUtils"
import { CONTENT_EDIT_ACTION,
         CONTENT_EDIT_STATUS,
         CONTENT_VERIFICATION_STATUS
       }                                from "types/cms/frontendTypes"
import PublishIcon                     from '@mui/icons-material/Publish'
import FileDownloadOffIcon             from '@mui/icons-material/FileDownloadOff'
import { getDisplayTitle } from "../utils/SurveyTemplateLocalizationUtils"

interface SurveyTemplateProps {
  template : APIData<'api::survey-template.survey-template'>
  onTemplateDeleted : () => void
  editable : boolean
  isDraftOrModified ?: boolean
  isPublished ?: boolean
  selectedLocale : string
}

export const SurveyTemplate = ({ template, onTemplateDeleted, editable, isDraftOrModified, isPublished, selectedLocale }: SurveyTemplateProps) => {

  const theme = useTheme()
  const dispatch = useDispatch()
  const { rc } = useGlobalConfig()

  const selectedPilotDocId = useSelector(selectCMSSelectedPilotDocId)
  const publishedQuestions = useSelector(selectPublishedQuestions)
  const unsavedTemplates = useSelector(selectUnsavedTemplates)
  const publishedTemplates = useSelector(selectPublishedTemplates)
  const reviewWorkflow = useSelector(selectReviewWorkflow)
  
  const publishedTemplate = publishedTemplates.find(t => t.documentId === template.documentId)
  
  const verificationStatus = (template as any).verificationStatus as CONTENT_VERIFICATION_STATUS
  const editStatus = (template as any).editStatus as CONTENT_EDIT_STATUS

  const displayTemplate = useMemo(() => {
    if (isPublished) {
      return template
    }
    const unsavedDiff = unsavedTemplates[template.documentId]
    if (unsavedDiff && Object.keys(unsavedDiff).length > 0) {
      return { ...template, ...unsavedDiff } as APIData<'api::survey-template.survey-template'>
    }
    return template
  }, [template, unsavedTemplates, isPublished])

  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set())
  const [draggedQuestionNumber, setDraggedQuestionNumber] = useState<string | null>(null)
  const [dragOverQuestionNumber, setDragOverQuestionNumber] = useState<string | null>(null)
  const [isImportDrawerOpen, setIsImportDrawerOpen] = useState(false)
  const [importDrawerMode, setImportDrawerMode] = useState<'primary' | 'child'>('primary')
  const [childQuestionContext, setChildQuestionContext] = useState<{
    parentQuestionUid: string
    parentChoiceValue: string
    parentChoiceTitle: string
    parentChoiceReadMapper: string
  } | undefined>(undefined)
  const [isSaving, setIsSaving] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'error' | 'success' | 'info' | 'warning'
  }>({
    open: false,
    message: '',
    severity: 'info',
  })
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    questionUid: string | null
    questionTitle: string
    hasChildQuestions: boolean
  }>({
    open: false,
    questionUid: null,
    questionTitle: '',
    hasChildQuestions: false,
  })
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [editNameDialogOpen, setEditNameDialogOpen] = useState(false)
  const [editQuestionDialogOpen, setEditQuestionDialogOpen] = useState(false)
  const [editQuestionUid, setEditQuestionUid] = useState<string | null>(null)
  const [editQuestionTitle, setEditQuestionTitle] = useState<string>('')
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false)
  
  // Check if this template has unsaved changes from Redux state
  const hasUnsavedChanges = useMemo(() => {
    const unsavedDiff = unsavedTemplates[template.documentId]
    return unsavedDiff !== undefined && Object.keys(unsavedDiff).length > 0
  }, [template.documentId, unsavedTemplates])
  
  // Get workflow state and action permissions (after hasUnsavedChanges is defined)
  const workflowState = VerificationWorkflowUtils.getNextWorkflowState(rc, verificationStatus, reviewWorkflow!)
  
  // Get allowed verification status transitions based on workflow
  const allowedVerificationStatuses = useMemo(() => {
    if (hasUnsavedChanges || isPublished) {
      return [verificationStatus]
    }
    return [verificationStatus, ...workflowState.nextQaStatus]
  }, [workflowState, hasUnsavedChanges, isPublished, verificationStatus])
  
  // Check if actions are allowed based on workflow
  const isDiscardAllowed = VerificationWorkflowUtils.isActionAllowed(CONTENT_EDIT_ACTION.DISCARD_UPDATE, workflowState)
  const isSaveAllowed = VerificationWorkflowUtils.isActionAllowed(CONTENT_EDIT_ACTION.SAVE_UPDATE, workflowState)
  const isPublishAllowed = VerificationWorkflowUtils.isActionAllowed(CONTENT_EDIT_ACTION.PUBLISH, workflowState)
  const isUnpublishAllowed = VerificationWorkflowUtils.isActionAllowed(CONTENT_EDIT_ACTION.UNPUBLISH, workflowState)
  const isDeleteAllowed = VerificationWorkflowUtils.isActionAllowed(CONTENT_EDIT_ACTION.DELETE, workflowState)
  
  // All edits are only allowed when verification status is TODO
  const isEditable = editable && verificationStatus === CONTENT_VERIFICATION_STATUS.TODO
  
  const trackTemplateChanges = useCallback((updatedTemplate: Partial<APIData<'api::survey-template.survey-template'>>) => {
    
    let diff: Partial<APIData<'api::survey-template.survey-template'>>
    
    // Draft
    if (!publishedTemplate) {
      diff = SurveyTemplateUtils.getTemplateDiff(updatedTemplate, template)
    } else {
      // Modified
      diff = SurveyTemplateUtils.getTemplateDiff(updatedTemplate, publishedTemplate)
    }

    // Only store if there are actual changes (getTemplateDiff returns only differences)
    if (Object.keys(diff).length > 0) {
      dispatch(setUnsavedTemplateAction({
        documentId: updatedTemplate.documentId!,
        diff
      }))
    } else {
      dispatch(discardUnsavedTemplateAction(updatedTemplate.documentId!))
    }
  }, [dispatch, template, publishedTemplate])

  // Build tree structure from template questions (use displayTemplate to show unsaved changes)
  const questionTree = useMemo(() => {
    if (!displayTemplate?.templateQuestions) return []

    // Get primary questions (isPrimary: true) sorted by displayOrder
    const primaryQuestions = displayTemplate.templateQuestions
      .filter(tq => tq.isPrimary)
      .sort((a, b) => a.displayOrder - b.displayOrder)

    // Build a map of documentId (stored as questionUid) to templateQuestion for quick lookup
    const templateQuestionMap = new Map(
      displayTemplate.templateQuestions.map(tq => [tq.questionUid, tq])
    )

    // Find question by documentId (helper function inside useMemo)
    const findQuestionByDocId = (documentId: string): APIData<'api::survey-question.survey-question'> | null => {
      return publishedQuestions.find(q => q.documentId === documentId) || null
    }

    // Recursive function to build tree nodes
    const buildTreeNode = (
      templateQuestion: typeof template.templateQuestions[0],
      questionNumber: string,
      level: number,
      parentChoiceValue?: string
    ): QuestionTreeNode | null => {
      // templateQuestion.questionUid actually contains documentId
      const question = findQuestionByDocId(templateQuestion.questionUid)
      
      // Skip if question not found in published questions
      if (!question) {
        return null
      }
      
      // Find children for this question based on branching rules
      const children: QuestionTreeNode[] = []
      if (templateQuestion.branchingRules && templateQuestion.branchingRules.length > 0) {
        // Group children by parent choice value, tracking both childTq and rule's displayOrder
        const childrenByChoice = new Map<string, Array<{
          childTq: typeof template.templateQuestions[0]
          displayOrder: number
        }>>()
        
        templateQuestion.branchingRules.forEach(rule => {
          // rule.childQuestionUid actually contains documentId
          // First try to find in templateQuestions, if not found, create a minimal template question object
          let childTq = templateQuestionMap.get(rule.childQuestionUid)
          
          // If child question is not in templateQuestions, create a minimal template question from the branching rule
          if (!childTq) {
            // Check if the question exists in published questions
            const childQuestion = findQuestionByDocId(rule.childQuestionUid)
            if (childQuestion) {
              // Check if this child question has its own branching rules defined in templateQuestions
              const childTemplateQuestion = template.templateQuestions.find(
                tq => tq.questionUid === rule.childQuestionUid
              )
              
              // Create a minimal template question object for the child
              childTq = {
                id: 0, // Temporary ID
                isPrimary: false,
                displayOrder: 0, // Not used - displayOrder is on branching rule
                title: childTemplateQuestion?.title || null,
                questionUid: rule.childQuestionUid, // documentId
                branchingRules: childTemplateQuestion?.branchingRules || [], // Use branching rules if found
              } as typeof template.templateQuestions[0]
            }
          }
          
          if (childTq) {
            const ruleDisplayOrder = rule.displayOrder || 0
            if (!childrenByChoice.has(rule.parentChoiceValue)) {
              childrenByChoice.set(rule.parentChoiceValue, [])
            }
            childrenByChoice.get(rule.parentChoiceValue)!.push({
              childTq,
              displayOrder: ruleDisplayOrder
            })
          }
        })

        // Sort children within each choice group by the rule's displayOrder
        childrenByChoice.forEach((childrenWithOrder) => {
          childrenWithOrder.sort((a, b) => a.displayOrder - b.displayOrder)
        })

        // Build children nodes, maintaining order by choice value
        const sortedChoices = Array.from(childrenByChoice.entries()).sort((a, b) => {
          // Sort by the order of choices in the parent question
          // templateQuestion.questionUid actually contains documentId
          const parentQuestion = findQuestionByDocId(templateQuestion.questionUid)
          if (parentQuestion?.choices) {
            const aIndex = parentQuestion.choices.findIndex(c => c.value === a[0])
            const bIndex = parentQuestion.choices.findIndex(c => c.value === b[0])
            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
            if (aIndex !== -1) return -1
            if (bIndex !== -1) return 1
          }
          return 0
        })

        let childIndex = 1
        sortedChoices.forEach(([parentChoiceValue, childrenWithOrder]) => {
          childrenWithOrder.forEach(({ childTq }) => {
            const childNumber = `${questionNumber}.${childIndex}`
            const childNode = buildTreeNode(childTq, childNumber, level + 1, parentChoiceValue)
            if (childNode) {
              children.push(childNode)
              childIndex++
            }
          })
        })
      }

      return {
        templateQuestion,
        question,
        children,
        questionNumber,
        level,
        parentChoiceValue,
      }
    }

    // Build tree starting from primary questions
    let primaryIndex = 1
    return primaryQuestions
      .map(tq => {
        const node = buildTreeNode(tq, `Q${primaryIndex}`, 0)
        if (node) {
          primaryIndex++
          return node
        }
        return null
      })
      .filter((node): node is QuestionTreeNode => node !== null)
  }, [displayTemplate, publishedQuestions, template])


  const toggleExpand = useCallback((questionNumber: string) => {
    setExpandedQuestions(prev => {
      const next = new Set(prev)
      if (next.has(questionNumber)) {
        next.delete(questionNumber)
      } else {
        next.add(questionNumber)
      }
      return next
    })
  }, [])

  // Handle drag start
  const handleDragStart = useCallback((e: React.DragEvent, questionNumber: string) => {
    setDraggedQuestionNumber(questionNumber)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', questionNumber)
  }, [])

  // Handle drag over
  const handleDragOver = useCallback((e: React.DragEvent, questionNumber: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (questionNumber !== draggedQuestionNumber) {
      setDragOverQuestionNumber(questionNumber)
    }
  }, [draggedQuestionNumber])

  // Handle drag leave
  const handleDragLeave = useCallback(() => {
    setDragOverQuestionNumber(null)
  }, [])

  // Handle drop - reorder primary questions
  const handleDrop = useCallback((e: React.DragEvent, targetQuestionNumber: string) => {
    e.preventDefault()
    setDragOverQuestionNumber(null)
    
    if (!draggedQuestionNumber || !displayTemplate || draggedQuestionNumber === targetQuestionNumber) {
      setDraggedQuestionNumber(null)
      return
    }

    // Find the dragged and target nodes in the tree
    const findNodeByNumber = (nodes: QuestionTreeNode[], num: string): QuestionTreeNode | null => {
      for (const node of nodes) {
        if (node.questionNumber === num) return node
        const found = findNodeByNumber(node.children, num)
        if (found) return found
      }
      return null
    }

    const draggedNode = findNodeByNumber(questionTree, draggedQuestionNumber)
    const targetNode = findNodeByNumber(questionTree, targetQuestionNumber)

    if (!draggedNode || !targetNode || draggedNode.level !== 0 || targetNode.level !== 0) {
      // Only allow reordering of primary questions (level 0)
      setDraggedQuestionNumber(null)
      return
    }

    // Get all primary questions in current order
    const primaryQuestions = displayTemplate.templateQuestions
      .filter(tq => tq.isPrimary)
      .sort((a, b) => a.displayOrder - b.displayOrder)

    // Find indices
    const draggedIndex = primaryQuestions.findIndex(tq => tq.questionUid === draggedNode.templateQuestion.questionUid)
    const targetIndex = primaryQuestions.findIndex(tq => tq.questionUid === targetNode.templateQuestion.questionUid)

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedQuestionNumber(null)
      return
    }

    // Reorder the array
    const reordered = [...primaryQuestions]
    const [removed] = reordered.splice(draggedIndex, 1)
    reordered.splice(targetIndex, 0, removed)

    // Update displayOrder for all affected questions
    // Note: questionUid in templateQuestion actually contains documentId
    const updatedTemplateQuestions = displayTemplate.templateQuestions.map(tq => {
      const newIndex = reordered.findIndex(rq => rq.questionUid === tq.questionUid)
      if (newIndex !== -1 && tq.isPrimary) {
        return { ...tq, displayOrder: newIndex + 1 }
      }
      return tq
    })

    // Create updated template with new order (only track changes, don't update Redux yet)
    const updatedTemplate = {
      documentId: displayTemplate.documentId!,
      templateQuestions: updatedTemplateQuestions,
    }

    // Track changes in unsavedTemplates (don't update Redux template until save)
    trackTemplateChanges(updatedTemplate)
    setDraggedQuestionNumber(null)
  }, [draggedQuestionNumber, displayTemplate, questionTree, trackTemplateChanges])

  // Handle delete question
  const handleDeleteQuestion = useCallback((questionUid: string) => {
    if (!displayTemplate) return

    let hasChildQuestions = false
    const questionToDelete = displayTemplate.templateQuestions.find(tq => tq.questionUid === questionUid)
    if (questionToDelete) {
      hasChildQuestions = !!(questionToDelete.branchingRules && questionToDelete.branchingRules.length > 0)
    }

    // Find the question in published questions to get the title
    const publishedQuestion = publishedQuestions.find(q => q.documentId === questionUid)
    if (!publishedQuestion) return
    
    const questionTitle = questionToDelete 
      ? getDisplayTitle(displayTemplate, questionToDelete, publishedQuestion, selectedLocale)
      : getDisplayTitle(displayTemplate,
          { questionUid, isPrimary: false, displayOrder: 0, title: undefined } as any,
          publishedQuestion,
          selectedLocale
        )

    // Open delete dialog
    setDeleteDialog({
      open: true,
      questionUid,
      questionTitle,
      hasChildQuestions,
    })
  }, [displayTemplate, publishedQuestions, selectedLocale])


  // Helper function to find branching rules for a question
  const findBranchingRulesForQuestion = useCallback((questionUid: string) => {
    if (!displayTemplate) return []
    
    // Check if question exists in templateQuestions with branching rules
    const existingTq = displayTemplate.templateQuestions.find(tq => tq.questionUid === questionUid)
    if (existingTq && existingTq.branchingRules) {
      return existingTq.branchingRules
    }
    
    // If question doesn't exist in templateQuestions, it can't have branching rules
    // (branching rules are only stored on template question entries)
    return []
  }, [displayTemplate])

  // Helper function to find a question in templateQuestions or create it from branching rules
  const findOrCreateTemplateQuestion = useCallback((questionUid: string): APIData<'api::survey-template.survey-template'>['templateQuestions'][0] | null => {
    if (!displayTemplate) return null

    // First try to find in templateQuestions
    const templateQuestion = displayTemplate.templateQuestions.find(tq => tq.questionUid === questionUid)
    
    if (templateQuestion) {
      return templateQuestion
    }

    // If not found, check if question exists in published questions
    const publishedQuestion = publishedQuestions.find(q => q.documentId === questionUid)
    if (!publishedQuestion) return null

    // Find existing branching rules for this question (if any)
    const foundBranchingRules = findBranchingRulesForQuestion(questionUid)

    // Create a minimal template question object for the child
    return {
      id: 0, // Will be assigned by backend
      isPrimary: false,
      displayOrder: 0,
      title: undefined,
      questionUid: questionUid,
      branchingRules: foundBranchingRules,
    } as typeof displayTemplate.templateQuestions[0]
  }, [displayTemplate, publishedQuestions, findBranchingRulesForQuestion])


  const handleEditQuestion = useCallback((questionUid: string) => {
    if (!displayTemplate) return
    
    // Find or create the templateQuestion by questionUid (handles child questions that may not have direct entry)
    const templateQuestion = findOrCreateTemplateQuestion(questionUid)
    
    if (!templateQuestion) return
    
    // Get the question title - use templateQuestion.title if present, otherwise use published question title
    const publishedQuestion = publishedQuestions.find(q => q.documentId === questionUid)
    const questionTitle = getDisplayTitle(displayTemplate, templateQuestion, publishedQuestion || null, selectedLocale)
    
    setEditQuestionUid(questionUid)
    setEditQuestionTitle(questionTitle)
    setEditQuestionDialogOpen(true)
  }, [displayTemplate, publishedQuestions, selectedLocale, findOrCreateTemplateQuestion])
  
  const handleEditQuestionClose = useCallback(() => {
    setEditQuestionDialogOpen(false)
    setEditQuestionUid(null)
    setEditQuestionTitle('')
  }, [])

  // Confirm delete question
  const handleConfirmDelete = useCallback(() => {
    if (!displayTemplate || !deleteDialog.questionUid) return

    // Remove the question from templateQuestions
    const updatedTemplateQuestions = displayTemplate.templateQuestions.filter(
      tq => tq.questionUid !== deleteDialog.questionUid
    )

    // Also remove any branching rules in other questions that reference this question as a child
    const cleanedTemplateQuestions = updatedTemplateQuestions.map(tq => {
      if (tq.branchingRules && tq.branchingRules.length > 0) {
        const filteredBranchingRules = tq.branchingRules.filter(
          rule => rule.childQuestionUid !== deleteDialog.questionUid
        )
        return {
          ...tq,
          branchingRules: filteredBranchingRules,
        }
      }
      return tq
    })

    // Create updated template (only track changes, don't update Redux yet)
    const updatedTemplate = {
      documentId: displayTemplate.documentId!,
      templateQuestions: cleanedTemplateQuestions,
    }

    // Track changes in unsavedTemplates (don't update Redux template until save)
    trackTemplateChanges(updatedTemplate)
    
    // Close dialog and show success message
    setDeleteDialog({ open: false, questionUid: null, questionTitle: '', hasChildQuestions: false })
  }, [displayTemplate, deleteDialog, trackTemplateChanges])

  // Get set of existing question IDs (both primary and child questions from all branching rules)
  const existingQuestionIds = useMemo(() => {
    if (!displayTemplate?.templateQuestions) return new Set<string>()
    
    const questionIds = new Set<string>()
    const processedQuestions = new Set<string>() // Track processed template questions to avoid duplicates
    
    // Recursive function to collect all question IDs including nested child questions
    const collectQuestionIds = (templateQuestion: typeof displayTemplate.templateQuestions[0]) => {
      const questionUid = templateQuestion.questionUid
      
      // Skip if already processed (prevents infinite recursion and duplicate processing)
      if (processedQuestions.has(questionUid)) return
      processedQuestions.add(questionUid)
      questionIds.add(questionUid)
      
      // Recursively collect child questions from branching rules
      if (templateQuestion.branchingRules && templateQuestion.branchingRules.length > 0) {
        templateQuestion.branchingRules.forEach(rule => {
          // Add the child question ID (even if it doesn't have a template question entry)
          questionIds.add(rule.childQuestionUid)
          
          // Check if this child question has its own template question entry with branching rules
          const childTemplateQuestion = displayTemplate.templateQuestions.find(
            tq => tq.questionUid === rule.childQuestionUid
          )
          
          // If found, recursively collect its children
          if (childTemplateQuestion) {
            collectQuestionIds(childTemplateQuestion)
          }
        })
      }
    }
    
    // Collect all question IDs from all template questions
    displayTemplate.templateQuestions.forEach(collectQuestionIds)
    
    return questionIds
  }, [displayTemplate])


  // Handle adding child question - open drawer with context
  const handleAddChildQuestion = useCallback((parentQuestionUid: string, parentChoiceValue: string, parentChoiceTitle: string, parentChoiceReadMapper: string) => {
    if (!displayTemplate) return

    // Find or create the parent question
    let parentTemplateQuestion = findOrCreateTemplateQuestion(parentQuestionUid)
    
    if (!parentTemplateQuestion) {
      setSnackbar({
        open: true,
        message: 'Parent question not found',
        severity: 'error',
      })
      return
    }

    // Check if parent question is already in templateQuestions, if not, add it
    const existsInTemplate = displayTemplate.templateQuestions.some(tq => tq.questionUid === parentQuestionUid)
    if (!existsInTemplate) {
      // Add the parent question to templateQuestions so it can have branching rules
      const updatedTemplateQuestions = [...displayTemplate.templateQuestions, parentTemplateQuestion]
      const updatedTemplate = {
        documentId: displayTemplate.documentId!,
        templateQuestions: updatedTemplateQuestions,
      }
      // Track changes in unsavedTemplates (don't update Redux template until save)
      trackTemplateChanges(updatedTemplate)
      // Update local reference
      parentTemplateQuestion = updatedTemplateQuestions.find(tq => tq.questionUid === parentQuestionUid)!
    }

    // Find the parent question's level in the tree
    const findQuestionLevel = (questionUid: string, nodes: QuestionTreeNode[], currentLevel: number = 0): number | null => {
      for (const node of nodes) {
        if (node.templateQuestion.questionUid === questionUid) {
          return currentLevel
        }
        const found = findQuestionLevel(questionUid, node.children, currentLevel + 1)
        if (found !== null) return found
      }
      return null
    }

    const parentLevel = findQuestionLevel(parentQuestionUid, questionTree)
    
    // The child question will be at parentLevel + 1
    // maxLevel means the maximum level the question can be nested at:
    // - maxLevel = 0: can only be at level 0 (primary)
    // - maxLevel = 1: can be at level 0 or 1 (primary or child)
    // - maxLevel = 2: can be at level 0, 1, or 2 (primary, child, or sub-child)
    // We'll validate the child question's maxLevel when it's selected in handleAddQuestion
    // For now, just check if parent is at level 2 (can't add children to level 2 questions)
    if (parentLevel !== null && parentLevel >= 2) {
      setSnackbar({
        open: true,
        message: 'Cannot add child question. Maximum nesting level (2) has been reached.',
        severity: 'warning',
      })
      return
    }

    // Set context and open drawer
    setChildQuestionContext({ parentQuestionUid, parentChoiceValue, parentChoiceTitle, parentChoiceReadMapper })
    setImportDrawerMode('child')
    setIsImportDrawerOpen(true)
  }, [displayTemplate, findOrCreateTemplateQuestion, questionTree, trackTemplateChanges])

  const renderQuestionItem = useCallback((node: QuestionTreeNode, parentQuestion?: APIData<'api::survey-question.survey-question'> | null) => {
    const isExpanded = expandedQuestions.has(node.questionNumber)
    const isDragging = draggedQuestionNumber === node.questionNumber
    const isDragOver = dragOverQuestionNumber === node.questionNumber

    return (
      <SurveyTemplateQuestionItem
        node={node}
        parentQuestion={parentQuestion}
        isExpanded={isExpanded}
        onToggleExpand={toggleExpand}
        isDragging={isDragging}
        isDragOver={isDragOver}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onDelete={handleDeleteQuestion}
        onEditQuestion={handleEditQuestion}
        onAddChildQuestion={handleAddChildQuestion}
        renderChild={renderQuestionItem}
        editable={isEditable}
        selectedLocale={selectedLocale}
        displayTemplate={displayTemplate}
      />
    )
  }, [expandedQuestions, draggedQuestionNumber, dragOverQuestionNumber, toggleExpand, handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDeleteQuestion, handleEditQuestion, handleAddChildQuestion, isEditable, selectedLocale, displayTemplate])

  // Handle adding a question from the question bank (primary or child)
  const handleAddQuestion = useCallback((question: APIData<'api::survey-question.survey-question'>) => {
    if (!displayTemplate) return

    // For primary questions, check if already exists
    if (importDrawerMode === 'primary') {
      if (existingQuestionIds.has(question.documentId)) {
        return
      }
    }

    // If adding as child question
    if (importDrawerMode === 'child' && childQuestionContext) {
      const { parentQuestionUid, parentChoiceValue } = childQuestionContext
      
      // Find or create the parent question
      let parentTemplateQuestion = findOrCreateTemplateQuestion(parentQuestionUid)
      
      if (!parentTemplateQuestion) {
        setSnackbar({
          open: true,
          message: 'Parent question not found',
          severity: 'error',
        })
        return
      }

      // Ensure parent question is in templateQuestions (add if not present)
      let updatedTemplateQuestions = [...displayTemplate.templateQuestions]
      const parentExists = updatedTemplateQuestions.some(tq => tq.questionUid === parentQuestionUid)
      
      if (!parentExists) {
        updatedTemplateQuestions.push(parentTemplateQuestion)
      }

      // Get the latest version of parent question from the array
      parentTemplateQuestion = updatedTemplateQuestions.find(tq => tq.questionUid === parentQuestionUid)!

      // For child questions: check maxLevel to allow multiple parents
      const questionMaxLevel = question.maxLevel ?? 1
      if (questionMaxLevel <= 1 && existingQuestionIds.has(question.documentId)) {
        // Question with maxLevel <= 1 already exists, cannot add again
        return
      }
      // If maxLevel > 1, continue even if question exists (can be child of multiple parents)

      // Check if this child question is already linked to this parent with this choice value
      const existingBranchingRule = parentTemplateQuestion.branchingRules?.find(
        rule => rule.parentChoiceValue === parentChoiceValue && 
                rule.childQuestionUid === question.documentId
      )
      
      if (existingBranchingRule) {
        setSnackbar({
          open: true,
          message: 'This question is already linked to this parent for this choice',
          severity: 'warning',
        })
        return
      }

      // Check if child question already exists in templateQuestions (for reuse)
      let childTemplateQuestion = updatedTemplateQuestions.find(
        tq => tq.questionUid === question.documentId
      )

      // If child doesn't exist, create it (displayOrder is set on branching rule, not here)
      if (!childTemplateQuestion) {
        childTemplateQuestion = {
          id: 0, // Will be assigned by backend
          isPrimary: false,
          displayOrder: 0, // Not used for child questions - displayOrder is on branching rule
          questionUid: question.documentId,
          branchingRules: [],
        } as typeof displayTemplate.templateQuestions[0]
        updatedTemplateQuestions.push(childTemplateQuestion)
      }

      // Find parent question's level to determine where child will be placed
      const findQuestionLevel = (questionUid: string, nodes: QuestionTreeNode[], currentLevel: number = 0): number | null => {
        for (const node of nodes) {
          if (node.templateQuestion.questionUid === questionUid) {
            return currentLevel
          }
          const found = findQuestionLevel(questionUid, node.children, currentLevel + 1)
          if (found !== null) return found
        }
        return null
      }

      const parentLevel = findQuestionLevel(parentQuestionUid, questionTree)
      const childLevel = parentLevel !== null ? parentLevel + 1 : 1

      // Validate child question's maxLevel
      // maxLevel means the maximum level the question can be nested at:
      // - maxLevel = 0: can only be at level 0 (primary)
      // - maxLevel = 1: can be at level 0 or 1 (primary or child)
      // - maxLevel = 2: can be at level 0, 1, or 2 (primary, child, or sub-child)
      const childMaxLevel = question.maxLevel ?? 1
      if (childMaxLevel < childLevel) {
        setSnackbar({
          open: true,
          message: `Cannot add question. This question's maximum nesting level (${childMaxLevel}) does not allow it to be placed at level ${childLevel}.`,
          severity: 'warning',
        })
        return
      }

      // Calculate displayOrder for the new branching rule based on existing rules for this parent's choice
      const existingRulesForChoice = parentTemplateQuestion.branchingRules
        ?.filter(rule => rule.parentChoiceValue === parentChoiceValue) || []

      const maxDisplayOrder = existingRulesForChoice.length > 0
        ? Math.max(...existingRulesForChoice.map(rule => rule.displayOrder || 0))
        : 0

      const nextDisplayOrder = maxDisplayOrder + 1

      // Create new branching rule with displayOrder
      const newBranchingRule = {
        id: 0, // Will be assigned by backend
        parentChoiceValue: parentChoiceValue,
        childQuestionUid: question.documentId,
        displayOrder: nextDisplayOrder, // Set displayOrder on the branching rule
      }

      // Add branching rule to parent question
      updatedTemplateQuestions = updatedTemplateQuestions.map(tq => {
        if (tq.questionUid === parentQuestionUid) {
          return {
            ...tq,
            branchingRules: [...(tq.branchingRules || []), newBranchingRule],
          }
        }
        return tq
      })

      // Create updated template (only track changes, don't update Redux yet)
      const updatedTemplate = {
        documentId: displayTemplate.documentId!,
        templateQuestions: updatedTemplateQuestions,
      }

      // Track changes in unsavedTemplates (don't update Redux template until save)
      trackTemplateChanges(updatedTemplate)
      
      // Close drawer and reset context
      setIsImportDrawerOpen(false)
      setImportDrawerMode('primary')
      setChildQuestionContext(undefined)
      
      setSnackbar({
        open: true,
        message: 'Child question added successfully',
        severity: 'success',
      })
      
      return
    }

    // Adding as primary question (existing logic)
    // Get the maximum displayOrder for primary questions
    const primaryQuestions = displayTemplate.templateQuestions
      .filter(tq => tq.isPrimary)
      .sort((a, b) => b.displayOrder - a.displayOrder)
    
    const nextDisplayOrder = primaryQuestions.length > 0 
      ? primaryQuestions[0].displayOrder + 1 
      : 1

    // Create new template question
    const newTemplateQuestion: typeof displayTemplate.templateQuestions[0] = {
      id: 0, // Will be assigned by backend
      isPrimary: true,
      displayOrder: nextDisplayOrder,
      questionUid: question.documentId,
      branchingRules: [],
    }

    // Add to template questions
    const updatedTemplateQuestions = [...displayTemplate.templateQuestions, newTemplateQuestion]

    // Create updated template (only track changes, don't update Redux yet)
    const updatedTemplate = {
      documentId: displayTemplate.documentId!,
      templateQuestions: updatedTemplateQuestions,
    }

    // Track changes in unsavedTemplates (don't update Redux template until save)
    trackTemplateChanges(updatedTemplate)
  }, [displayTemplate, existingQuestionIds, importDrawerMode, childQuestionContext, findOrCreateTemplateQuestion, questionTree, trackTemplateChanges])

  const handleDiscard = useCallback(() => {
    if (!window.confirm('Are you sure you want to discard all unsaved changes?')) {
      return
    }
    
    // Clear unsaved state since we're reverting to published
    dispatch(discardUnsavedTemplateAction(template.documentId))
    setSnackbar({
      open: true,
      message: 'Changes discarded',
      severity: 'info',
    })
    
  }, [template.documentId, dispatch])

  // Menu handlers
  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget)
  }, [])

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null)
  }, [])

  // Edit Name handlers
  const handleEditNameOpen = useCallback(() => {
    setEditNameDialogOpen(true)
    handleMenuClose()
  }, [handleMenuClose])

  const handleEditNameClose = useCallback(() => {
    setEditNameDialogOpen(false)
  }, [])

  const handleDiscardDialogOpen = useCallback(() => {
    setDiscardDialogOpen(true)
  }, [])

  const handleDiscardDialogClose = useCallback(() => {
    setDiscardDialogOpen(false)
  }, [])

  // Handle verification status change
  const handleVerificationStatusChange = useCallback(async (newStatus: CONTENT_VERIFICATION_STATUS) => {
    if (newStatus === verificationStatus || isSaving) return
    
    setIsSaving(true)
    
    try {
      const payload = {
        documentId: template.documentId,
        verificationStatus: newStatus,
      }
      
      const resp = await SurveyTemplateAPI.updateAllTemplates(
        rc,
        {
          utilityDocumentId : selectedPilotDocId,
          data : [payload]
        }
      )
      
      if (resp.error) {
        const errorMsg = resp.error.message || 'Failed to update verification status'
        setSnackbar({
          open: true,
          message: errorMsg,
          severity: 'error',
        })
      } else if (resp.data && resp.data.templates && resp.data.templates.length > 0) {
        const updatedTemplate = resp.data.templates[0]
        dispatch(updateTemplatesAction({ templates: [updatedTemplate] }))
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'An unexpected error occurred'
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }, [verificationStatus, isSaving, template.documentId, rc, selectedPilotDocId, dispatch])

  // Handle Publish
  const handlePublish = useCallback(async () => {
    if (isSaving) return
    setIsSaving(true)
    
    try {
      const resp = await SurveyTemplateAPI.publishAllTemplates(rc, [template.documentId])
      
      if (resp.error) {
        setSnackbar({
          open: true,
          message: resp.error.message || 'Error publishing template',
          severity: 'error',
        })
      } else if (resp.data) {
        const publishedTemplates = resp.data
        dispatch(publishTemplatesAction(publishedTemplates))
        setSnackbar({
          open: true,
          message: 'Template published successfully',
          severity: 'success',
        })
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'An error occurred while publishing template',
        severity: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }, [template.documentId, rc, dispatch, isSaving])

  // Handle Unpublish
  const handleUnpublish = useCallback(async () => {
    if (isSaving) return
    setIsSaving(true)
    
    try {
      const resp = await SurveyTemplateAPI.unpublishAllTemplates(rc, [template.documentId])
      
      if (resp.error) {
        setSnackbar({
          open: true,
          message: resp.error.message || 'Error unpublishing template',
          severity: 'error',
        })
      } else if (resp.data) {
        const draftTemplates = resp.data
        dispatch(unpublishTemplatesAction(draftTemplates))
        setSnackbar({
          open: true,
          message: 'Template unpublished successfully',
          severity: 'success',
        })
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'An error occurred while unpublishing template',
        severity: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }, [template.documentId, rc, dispatch, isSaving])

  // Handle Discard Draft / Discard Modification - Opens confirmation dialog
  const handleDiscardDraft = useCallback(() => {
    handleDiscardDialogOpen()
  }, [handleDiscardDialogOpen])

  // Handle Discard Draft / Discard Modification Confirmation
  const handleDiscardDraftConfirm = useCallback(async () => {
    if (isSaving) return
    setIsSaving(true)
    setDiscardDialogOpen(false)
    
    try {
      const resp = await SurveyTemplateAPI.discardAllDraftTemplates(rc, selectedPilotDocId, [template.documentId])
      
      if (resp.error) {
        setSnackbar({
          open: true,
          message: resp.error.message || 'Error discarding template',
          severity: 'error',
        })
      } else {
        dispatch(discardAllDraftTemplatesAction([template.documentId]))
        setSnackbar({
          open: true,
          message: editStatus === CONTENT_EDIT_STATUS.DRAFT ? 'Draft deleted successfully' : 'Modification discarded successfully',
          severity: 'success',
        })
        if (editStatus === CONTENT_EDIT_STATUS.DRAFT) onTemplateDeleted()
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'An error occurred while discarding template',
        severity: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }, [template.documentId, rc, dispatch, isSaving, editStatus, onTemplateDeleted, selectedPilotDocId])

  const handleSave = useCallback(async () => {
    if (isSaving) return
    setIsSaving(true)
    
    try {
      const baseTemplate = publishedTemplate || template
      let diff = SurveyTemplateUtils.getTemplateDiff(displayTemplate, baseTemplate)
    
      // Remove the 'id' field from templateQuestions & branchingRules in the diff
      if (diff.templateQuestions) {
        const cleanedTemplateQuestions = diff.templateQuestions.map((tq: any) => {
          const { id, branchingRules, ...rest } = tq
          return {
            ...rest,
            branchingRules: branchingRules?.map((br: any) => {
              const { id, ...rest } = br
              return rest
            }) as any
          }
        }) as any
        
        diff = {
          ...diff,
          templateQuestions: cleanedTemplateQuestions
        }
      }
      
      // Include documentId in the diff for the API
      const payload = {
        documentId: displayTemplate.documentId,
        ...diff,
      }
      
      const resp = await SurveyTemplateAPI.updateAllTemplates(
        rc,
        {
          utilityDocumentId : selectedPilotDocId,
          data : [payload as any]
        }
      )
      
      if (resp.error) {
        const errorMsg = resp.error.message || 'Failed to save template'
        setSnackbar({
          open: true,
          message: errorMsg,
          severity: 'error',
        })
        return
      }
      
      if (resp.data && resp.data.templates && resp.data.templates.length > 0) {
        const updatedTemplate = resp.data.templates[0]
        dispatch(updateTemplatesAction({ templates: [updatedTemplate] }))
        dispatch(discardUnsavedTemplateAction(displayTemplate.documentId))
        setSnackbar({
          open: true,
          message: 'Template saved successfully',
          severity: 'success',
        })
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'An unexpected error occurred while saving'
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }, [displayTemplate, template, publishedTemplate, selectedPilotDocId, rc, dispatch, isSaving])

  return (
    <Box sx={{ mt: 3 }}>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {displayTemplate.label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Verification Status Dropdown */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ fontSize: '14px', color: theme.palette.text.secondary }}>QA Status</InputLabel>
            <Select
              value={verificationStatus}
              onChange={(e) => handleVerificationStatusChange(e.target.value as CONTENT_VERIFICATION_STATUS)}
              label="QA Status"
              disabled={hasUnsavedChanges || isPublished || isSaving || !editable}
              sx={{
                height: '36px',
                fontSize: '12px',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '.MuiSelect-select': {
                  minHeight: 'unset',
                  color: theme.palette.text.secondary
                }
              }}
            >
              {allowedVerificationStatuses.map((status) => (
                <MenuItem key={status} value={status} sx={{ fontSize: '0.875rem' }}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <BglPrimaryOutlinedSmallButton
            onClick={() => {}}
            startIcon={<Eye size={16} />}
          >
            Preview
          </BglPrimaryOutlinedSmallButton>
          <BglCloseOutlinedSmallButton
            onClick={handleDiscard}
            disabled={!hasUnsavedChanges || isSaving || !isEditable || !isDiscardAllowed}
            startIcon={<HistoryIcon />}
          >
            Discard
          </BglCloseOutlinedSmallButton>
          <BglSecondaryOutlinedSmallButton
            onClick={handleSave}
            disabled={!hasUnsavedChanges || isSaving || !isEditable || !isSaveAllowed}
            startIcon={<SaveIcon />}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </BglSecondaryOutlinedSmallButton>
          <IconButton
            onClick={handleMenuOpen}
            size="small"
            sx={{ ml: 1 }}
          >
            <MoreVertIcon sx={{ color: theme.palette.text.secondary }} />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleEditNameOpen} disabled={!isEditable}>
              <ListItemIcon >
                <Pencil size={16} style={{ color: theme.palette.text.secondary }} />
              </ListItemIcon>
              <ListItemText>Edit Name</ListItemText>
            </MenuItem>
            <MenuItem 
              disabled={!isPublishAllowed || isPublished || hasUnsavedChanges || !editable}
              onClick={handlePublish}
            >
              <ListItemIcon>
                <PublishIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
              </ListItemIcon>
              <ListItemText>Publish</ListItemText>
            </MenuItem>
            <MenuItem 
              disabled={!isUnpublishAllowed || isPublished || hasUnsavedChanges || !editable}
              onClick={handleUnpublish}
            >
              <ListItemIcon>
                <FileDownloadOffIcon fontSize="small" sx={{ transform: 'rotate(180deg)', color: theme.palette.text.secondary }} />
              </ListItemIcon>
              <ListItemText>Unpublish</ListItemText>
            </MenuItem>
            <MenuItem 
              disabled={!isDeleteAllowed || isPublished || hasUnsavedChanges || !editable}
              onClick={handleDiscardDraft}
              sx={{ color: theme.palette.error.main }}
            >
              <ListItemIcon>
                <Trash2 size={16} style={{ color: theme.palette.error.main }} />
              </ListItemIcon>
              <ListItemText>
                {editStatus === CONTENT_EDIT_STATUS.DRAFT ? 'Delete draft' : 'Discard modification'}
              </ListItemText>
            </MenuItem>
            
          </Menu>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ListTree size={20} style={{ color: theme.palette.text.secondary }} />
          <Typography sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
            Questions
          </Typography>
        </Box>
        <BglPrimaryOutlinedSmallButton
          disabled={!isEditable}
          onClick={() => {
            setImportDrawerMode('primary')
            setChildQuestionContext(undefined)
            setIsImportDrawerOpen(true)
          }}
          startIcon={<BookOpen size={16} />}
        >
          Import from Question Bank
        </BglPrimaryOutlinedSmallButton>
      </Box>

      {questionTree.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: theme.palette.background.default, p: 1, py: 4, textAlign: 'center' }}>
          Add questions from the bank to build your survey.
        </Typography>
      ) : (
        <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: theme.palette.background.default, p: 1 }}>
          {questionTree.map(node => renderQuestionItem(node))}
        </Box>
      )}

      <ImportQuestionBankDrawer
        open={isImportDrawerOpen}
        onClose={() => {
          setIsImportDrawerOpen(false)
          setImportDrawerMode('primary')
          setChildQuestionContext(undefined)
        }}
        onAddQuestion={handleAddQuestion}
        existingQuestionIds={existingQuestionIds}
        mode={importDrawerMode}
        childQuestionContext={childQuestionContext}
        selectedLocale={selectedLocale}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <BglAlertDialog
        open={deleteDialog.open}
        title={deleteDialog.hasChildQuestions ? "Cannot Delete Question" : "Delete Question"}
        message={
          deleteDialog.hasChildQuestions
            ? `Cannot delete "${deleteDialog.questionTitle}" because it has child questions. Please delete all child questions first before deleting this question.`
            : `Are you sure you want to delete "${deleteDialog.questionTitle}"?`
        }
        severity={deleteDialog.hasChildQuestions ? "warning" : "error"}
        onClose={() => setDeleteDialog({ open: false, questionUid: null, questionTitle: '', hasChildQuestions: false })}
        onConfirm={deleteDialog.hasChildQuestions ? undefined : handleConfirmDelete}
        confirmText={deleteDialog.hasChildQuestions ? 'Close' : 'Delete'}
        cancelText="Cancel"
        showCancel={!deleteDialog.hasChildQuestions}
      />

      {/* Edit Name Dialog */}
      <SurveyTemplateEditNameDialog
        open={editNameDialogOpen}
        onClose={handleEditNameClose}
        template={template}
        onTemplateChange={trackTemplateChanges}
      />

      {/* Edit Question Title Dialog */}
      {editQuestionUid && (
        <SurveyTemplateEditQuestionTitleDialog
          open={editQuestionDialogOpen}
          onClose={handleEditQuestionClose}
          template={displayTemplate}
          questionUid={editQuestionUid}
          questionTitle={editQuestionTitle}
          selectedLocale={selectedLocale}
        />
      )}

      {/* Discard Draft / Discard Modification Confirmation Dialog */}
      <BglAlertDialog
        open={discardDialogOpen}
        title={editStatus === CONTENT_EDIT_STATUS.DRAFT ? 'Delete Draft' : 'Discard Modification'}
        message={
          editStatus === CONTENT_EDIT_STATUS.DRAFT
            ? `Are you sure you want to delete the draft "${template.label}"? This action cannot be undone.`
            : `Are you sure you want to discard modifications to "${template.label}" and restore the published version?`
        }
        severity="warning"
        onClose={handleDiscardDialogClose}
        onConfirm={handleDiscardDraftConfirm}
        confirmText={isSaving ? (editStatus === CONTENT_EDIT_STATUS.DRAFT ? 'Deleting...' : 'Discarding...') : (editStatus === CONTENT_EDIT_STATUS.DRAFT ? 'Delete' : 'Discard')}
        cancelText="Cancel"
        showCancel={true}
      />
    </Box>
  )
}
